// MyInvoicesPage, lists the user's NF-e (Nota Fiscal) invoices

import { useEffect, useState } from 'react';
import { FileText, Download, AlertCircle, CheckCircle, Clock, XCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';

interface FiscalInvoice {
  id: string;
  invoice_number: string | null;
  amount_cents: number;
  service_description: string;
  status: 'pending' | 'issued' | 'canceled' | 'error';
  pdf_url: string | null;
  issued_at: string | null;
  created_at: string;
  error_message: string | null;
}

const STATUS_CONFIG = {
  issued:  { label: 'Emitida',    icon: CheckCircle, color: '#276749', bg: 'rgba(39,103,73,0.1)' },
  pending: { label: 'Pendente',   icon: Clock,       color: '#C9A84C', bg: 'rgba(201,168,76,0.1)' },
  error:   { label: 'Erro',       icon: AlertCircle, color: '#E53E3E', bg: 'rgba(229,62,62,0.1)' },
  canceled:{ label: 'Cancelada',  icon: XCircle,     color: '#666',    bg: 'rgba(102,102,102,0.1)' },
};

function formatBRL(cents: number) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(cents / 100);
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function MyInvoicesPage() {
  const { profile }          = useAuth();
  const [invoices, setInvoices] = useState<FiscalInvoice[]>([]);
  const [loading, setLoading]   = useState(true);
  const [filter, setFilter]     = useState<'all' | 'issued' | 'pending'>('all');

  useEffect(() => {
    if (!profile?.id) return;
    fetchInvoices();
  }, [profile?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function fetchInvoices() {
    setLoading(true);
    const { data } = await supabase
      .from('fiscal_invoices')
      .select('id, invoice_number, amount_cents, service_description, status, pdf_url, issued_at, created_at, error_message')
      .eq('user_id', profile!.id)
      .order('created_at', { ascending: false });
    setInvoices((data ?? []) as FiscalInvoice[]);
    setLoading(false);
  }

  const filtered = invoices.filter(inv => {
    if (filter === 'all') return true;
    return inv.status === filter;
  });

  return (
    <div className="page-container" style={{ maxWidth: '800px' }}>

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
        <FileText size={24} color="var(--petrol)" />
        <div>
          <h1 style={{ fontSize: 'var(--text-title)', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>
            Notas Fiscais
          </h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', margin: 0 }}>
            NF-e das suas assinaturas e compras
          </p>
        </div>
      </div>

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem' }}>
        {(['all', 'issued', 'pending'] as const).map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={filter === f ? 'btn btn-primary' : 'btn btn-ghost'}
            style={{ padding: '0.4rem 1rem', fontSize: 'var(--text-caption)' }}
          >
            {f === 'all' ? 'Todas' : f === 'issued' ? 'Emitidas' : 'Pendentes'}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
          <p style={{ color: 'var(--text-muted)' }}>Carregando...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
          <FileText size={40} color="var(--text-muted)" style={{ marginBottom: '1rem' }} />
          <p style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>
            {filter === 'all' ? 'Nenhuma nota fiscal encontrada.' : `Nenhuma NF ${filter === 'issued' ? 'emitida' : 'pendente'}.`}
          </p>
          <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)' }}>
            As notas fiscais são emitidas automaticamente após cada pagamento confirmado.
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {filtered.map(inv => {
            const cfg = STATUS_CONFIG[inv.status];
            const StatusIcon = cfg.icon;
            return (
              <div key={inv.id} className="card" style={{ padding: '1rem 1.25rem' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.25rem' }}>
                      {inv.invoice_number && (
                        <span style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: 'var(--text-label)' }}>
                          NF nº {inv.invoice_number}
                        </span>
                      )}
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                        backgroundColor: cfg.bg, color: cfg.color,
                        fontSize: '11px', fontWeight: 600,
                        padding: '2px 8px', borderRadius: '999px',
                      }}>
                        <StatusIcon size={11} />
                        {cfg.label}
                      </span>
                    </div>
                    <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-caption)', margin: '0 0 0.25rem' }}>
                      {inv.service_description}
                    </p>
                    <p style={{ color: 'var(--text-muted)', fontSize: '12px', margin: 0 }}>
                      {inv.issued_at ? formatDate(inv.issued_at) : formatDate(inv.created_at)}
                    </p>
                    {inv.status === 'error' && inv.error_message && (
                      <p style={{ color: '#E53E3E', fontSize: '12px', margin: '0.25rem 0 0' }}>
                        {inv.error_message}
                      </p>
                    )}
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.5rem', flexShrink: 0 }}>
                    <span style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: 'var(--text-label)' }}>
                      {formatBRL(inv.amount_cents)}
                    </span>
                    {inv.pdf_url && (
                      <a
                        href={inv.pdf_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-ghost"
                        style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', padding: '0.375rem 0.75rem', fontSize: '13px' }}
                      >
                        <Download size={14} />
                        PDF
                      </a>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <p style={{ color: 'var(--text-muted)', fontSize: '12px', textAlign: 'center', marginTop: '1.5rem' }}>
        Notas fiscais são mantidas por 5 anos conforme legislação brasileira.
        Dúvidas? <a href="mailto:contato@professya.com" style={{ color: 'var(--petrol)' }}>contato@professya.com</a>
      </p>
    </div>
  );
}
